import * as React from "react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Drawer,
  Grid,
  IconButton,
  Switch,
  Typography,
  Box,
  Stack,
  List,
  ListItem,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import {
  selectStudentAttendance,
  tryGetStudentAttendance,
  tryTickStudentAttendance,
} from "../../../store/slices/student-courses-attendances";
import ModalMessages from "../../layout/modal-messages";
import CustomColumnMenu from "../../layout/grid-column-menu";
import Spinner from "../../layout/spinner";
import moment from "moment";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const CoursesDrawer = ({
  openDrawer,
  setOpenDrawer,
  courseId,
  studentId,
  course,
  setConfirmationDialog,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const attendanceTable = useSelector(selectStudentAttendance);
  const [rows, setRows] = useState([]);

  const model = {
    studentId: studentId,
    courseId: courseId,
  };

  const saveAttendances = async (model) => {
    const action = await dispatch(
      tryTickStudentAttendance({ studentId, courseId, model })
    );

    if (action.payload.isValid) {
      setConfirmationDialog(true);
      setOpenDrawer(false);
    }
  };

  const initializeDataGrid = () => {
    return {
      ...rows,
      columns: {
        ...columns,
        columnVisibilityModel: {
          id: false,
        },
      },
      pagination: { paginationModel: { pageSize: 6 } },
    };
  };

  //Manejo de switch
  const handleChange = (params, value) => {
    const updatedRows = rows.map((row) =>
      row.id === params.id ? { ...row, studentValid: value } : row
    );
    setRows(updatedRows);
  };

  //beforeToday false indica que SI se filtren las clases hasta el dia de hoy
  const beforeToday = true;

  useEffect(() => {
    dispatch(tryGetStudentAttendance({ model, beforeToday }));
  }, []);

  useEffect(() => {
    if (attendanceTable.data) {
      setRows(
        attendanceTable.data.map((x) => {
          return {
            id: x.attendanceId,
            attendanceId: x.attendanceId,
            studentCourseId: x.studentCourseId,
            attendanceDate: x.attendanceDate,
            studentValid: x.studentValid ?? false,
            teacherValid: x.teacherValid ?? false,
          };
        })
      );
    }
  }, [attendanceTable.data]);

  const columns = [
    { field: "id", headerName: "Id", filterable: false },
    {
      field: "attendanceDate",
      headerAlign: "center",
      align: "center",
      flex: 0.3,
      headerName: "Clase",
      headerClassName: "headRow",
      width: 260,

      valueFormatter: (params) => {
        if (params.value === null) return "";
        return moment(params.value).format("ll");
      },
    },
    {
      field: "studentValid",
      headerAlign: "center",
      align: "center",
      flex: 0.3,
      headerName: "Asistencia",
      headerClassName: "headRow",
      width: 130,

      renderCell: (params) => {
        return (
          <Switch
            label={params.value}
            checked={params.value ?? false}
            color="success"
            onChange={(_, value) => {
              handleChange(params, value);
            }}
          />
        );
      },
    },
  ];

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={openDrawer}
      onClose={() => setOpenDrawer(false)}
      PaperProps={{
        sx: {
          zIndex: 1203,
          width: { xs: "100vw", sm: 500 },
          maxWidth: "100%",
          maxHeight: { xs: "90vh", sm: "100%" },
          borderRadius: { xs: "12px 12px 0 0", sm: 0 },
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        },
      }}
      sx={{
        zIndex: 1203,
        flexShrink: 0,
      }}
    >
      {/* Header Modal Azul Responsivo */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: "#194B6F",
          color: "white",
          position: "relative",
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 4 },
          pb: { xs: 1.5, sm: 3 },
        }}
      >
        <IconButton
          sx={{
            position: "absolute",
            top: { xs: 8, sm: 16 },
            right: { xs: 8, sm: 16 },
            color: "white",
          }}
          onClick={() => setOpenDrawer(false)}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          align="left"
          sx={{
            fontWeight: 700,
            fontSize: 18,
            mb: { xs: 0, sm: 1 },
            pr: 4,
          }}
        >
          Marcar Asistencia
        </Typography>

        <Typography
          variant="body2"
          align="left"
          sx={{ display: { xs: "none", sm: "block" } }}
        >
          La siguiente grilla muestra un detalle de la asistencia que usted
          mantiene en la actualidad con este curso.
        </Typography>
      </Box>

      {/* Contenido Modal */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          backgroundColor: "white",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Grid container rowSpacing={3} spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12}>
            <Typography
              style={{ color: "black", fontWeight: "bold", fontSize: 18 }}
            >
              {course}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            {isMobile ? (
              <>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ px: 1, pb: 1, borderBottom: '1px solid #e0e0e0' }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: 'text.secondary' }}>
                    Clase
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: 'text.secondary', pr: 1 }}>
                    Asistencia
                  </Typography>
                </Box>
                <List disablePadding>
                  {rows.map((row, index) => (
                    <React.Fragment key={row.id}>
                      <ListItem
                        disableGutters
                        sx={{
                          py: 1.5,
                          px: 1,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Typography
                          color="text.primary"
                          sx={{ flex: 1, fontSize: 15 }}
                        >
                          {moment(row.attendanceDate).format("ll")}
                        </Typography>
                        <Switch
                          checked={row.studentValid ?? false}
                          color="success"
                          onChange={(_, value) => handleChange(row, value)}
                        />
                      </ListItem>
                      {index < rows.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                  {rows.length === 0 && (
                    <Typography color="textSecondary" align="center" sx={{ mt: 2 }}>No hay clases registradas.</Typography>
                  )}
                </List>
              </>
            ) : (
              <DataGrid
                slots={{
                  columnMenu: CustomColumnMenu,
                }}
                autoHeight={true}
                rowSelection={false}
                getRowId={(row) => row.id}
                rows={rows}
                columns={columns}
                rowsPerPageOptions={[5]}
                initialState={initializeDataGrid()}
              />
            )}
          </Grid>

          <Grid item xs={12} sx={{ textAlign: "center" }}>
            {isMobile ? (
              <Stack direction="column" spacing={2} sx={{ mt: 1, mb: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => saveAttendances(rows)}
                  disabled={attendanceTable.isLoading}
                >
                  {attendanceTable.isLoading && <Spinner color="primary" />}
                  Guardar
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  disabled={attendanceTable.isLoading}
                  onClick={() => setOpenDrawer(false)}
                >
                  Cancelar
                </Button>
              </Stack>
            ) : (
              <Box sx={{ mt: 2, mb: 2, display: "inline-block" }}>
                <Button
                  variant="contained"
                  onClick={() => saveAttendances(rows)}
                  disabled={attendanceTable.isLoading}
                  sx={{ width: 100, marginRight: 2 }}
                >
                  {attendanceTable.isLoading && <Spinner color="primary" />}
                  Guardar
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={attendanceTable.isLoading}
                  sx={{ border: 2, width: 100 }}
                  onClick={() => setOpenDrawer(false)}
                >
                  Cancelar
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      {attendanceTable.errors && attendanceTable.errors.length > 0 && (
        <ModalMessages errors={attendanceTable.errors} />
      )}
    </Drawer>
  );
};

export default CoursesDrawer;